import Modal from '@risksmart-app/components/src/modal';
import { useTools } from '@risksmart-app/components/src/tools/useTools';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import { toTestType } from '@risksmart-app/domain/src/types/consts/test-type';
import type { FC } from 'react';
import { ModalWrapper } from 'src/components/form/form/ModalWrapper';
import { useWizardStore } from 'src/components/wizard/store/useWizardStore';
import { useGetTestResultById, useGetTestResults } from 'src/hooks/queries';
import { useSmartCopyAmplitude } from 'src/hooks/smart-copy';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useMultiParentFileUpdate } from '@/hooks/mutations/file/useFileUpdate';
import { useInsertControlTestResult } from '@/hooks/mutations/test-result/useInsertControlTestResult';
import { useUpdateTestResult } from '@/hooks/mutations/test-result/useUpdateTestResult';
import { useGetControlById } from '@/hooks/queries/control/useGetControlById';
import {
  type CreatedEntityDestination,
  CreatedEntityFileSaveError,
  saveFilesForOptionalCreatedEntity,
} from '@/utils/createWithFiles';
import { controlDetailsUrl } from '@/utils/urls';

import ConnectedControlTestOversightResultForm from '../../../assessments/oversight-assessments/forms/ConnectedControlTestOversightResultForm';
import type { AssessmentTypeEnum } from '../../../assessments/types';
import ConnectedInternalAuditControlTestResultForm from '../../../internal-audit/reports/forms/ConnectedInternalAuditControlTestResultForm';
import { buildCopiedTestResultDefaults } from '../forms/buildCopiedTestResultDefaults';
import TestResultForm from '../forms/TestResultForm';
import type { TestResultFormFieldsData } from '../forms/testResultSchema';
import { defaultValues } from '../forms/testResultSchema';
import { buildInsertControlTestResultArgs } from './buildInsertControlTestResultArgs';
import { CONTROL_TEST_SMART_COPY } from './performance/controlTestSmartCopy';

type Props = {
  onDismiss: (saved?: boolean) => void;
  parentControlId: string;
  Id?: string;
  sourceResultId?: string;
  assessmentMode: AssessmentTypeEnum;
};

const StandardTestResultModal: FC<Props> = ({
  onDismiss,
  parentControlId,
  Id,
  sourceResultId,
  assessmentMode,
}) => {
  const { updateFiles } = useMultiParentFileUpdate();
  const [toolsContent] = useTools();
  const { assessmentId: wizardAssessmentId } = useWizardStore();
  // Nothing clears the wizard store when the wizard finishes, so the
  // toolsContent guard is what stops a stale id being inherited outside it.
  const inheritsWizardAssessment =
    toolsContent === 'wizard' && wizardAssessmentId !== '';
  const { data: controlData, error } = useGetControlById({
    queryArgs: { controlId: parentControlId },
  });
  if (error) {
    throw error;
  }
  const control = controlData?.control[0];
  const { insertControlTestResult } = useInsertControlTestResult();
  const { updateTestResult: update } = useUpdateTestResult();
  const { events, logEvent } = useSmartCopyAmplitude({
    entityLabel: CONTROL_TEST_SMART_COPY.eventLabel,
  });
  const { data, loading, refetch } = useGetTestResultById({
    queryArgs: { testResultId: Id ?? '' },
    shouldSkip: !Id,
  });

  const isCopyMode = !Id && Boolean(sourceResultId);
  const { data: sourceData, loading: sourceLoading } = useGetTestResultById({
    queryArgs: { testResultId: sourceResultId ?? '' },
    shouldSkip: !isCopyMode,
  });

  const { refetch: refetchTestResults } = useGetTestResults({ queryArgs: {} });

  const testResult = data?.test_result[0];
  const sourceTestResult = sourceData?.test_result[0];
  const {
    hasPermission: canEditTestResult,
    loading: canEditTestResultLoading,
  } = useHasPermissionQuery('update:test_result', control);
  const {
    hasPermission: canCreateTestResult,
    loading: canCreateTestResultLoading,
  } = useHasPermissionQuery('insert:test_result', control);

  const canModify = testResult
    ? canEditTestResult && !canEditTestResultLoading
    : canCreateTestResult && !canCreateTestResultLoading;

  const onSave = async (data: TestResultFormFieldsData) => {
    const { files } = data;
    let parentIds: string[];
    let createdEntity: CreatedEntityDestination | undefined;
    if (testResult) {
      const {
        ParentControlIds: _,
        files: __,
        AssessmentId: ___,
        ...rest
      } = data;
      const result = await update({
        ...rest,
        OriginalTimestamp: testResult.ModifiedAtTimestamp,
        Id: testResult.Id,
        ParentControlId: testResult.ParentControlId,
        CustomAttributeData: rest.CustomAttributeData || undefined,
        Submitter: data.Submitter.value,
      });
      if (!result.updateTestResultApi?.Id) {
        throw new Error(
          'Records not updated. Record may have been updated by another user'
        );
      }
      parentIds = [testResult.Id];
      refetch();
    } else {
      const result = await insertControlTestResult(
        buildInsertControlTestResultArgs(data)
      );
      parentIds = result.insertControlTestResult?.Ids ?? [];
      if (parentIds.length === 0) {
        throw new Error('Id not found');
      }
      createdEntity = {
        id: parentIds[0],
        destination: controlDetailsUrl(parentControlId),
      };
    }

    const saveFiles = () =>
      updateFiles({
        parentType: Parent_Type_Enum.TestResult,
        parentIds,
        originalFiles: testResult?.files.map((f) => f.file),
        selectedFiles: files,
      });
    const recordPrimarySave = () => {
      if (!testResult) {
        if (isCopyMode) {
          logEvent(events.SMART_COPY_SAVED, {
            controlId: parentControlId,
            sourceResultId,
          });
        } else {
          logEvent(events.BASELINE_SAVED, {
            controlId: parentControlId,
          });
        }
      }

      void refetchTestResults().catch(() => undefined);
    };

    try {
      await saveFilesForOptionalCreatedEntity({ createdEntity, saveFiles });
    } catch (error) {
      if (error instanceof CreatedEntityFileSaveError) {
        recordPrimarySave();
      }
      throw error;
    }

    recordPrimarySave();

    return {
      savedIds: parentIds,
      createdEntity,
    };
  };

  if (loading || sourceLoading) {
    return null;
  }

  const copiedDefaults =
    isCopyMode && sourceTestResult
      ? buildCopiedTestResultDefaults(sourceTestResult, parentControlId)
      : undefined;

  return (
    <TestResultForm
      values={
        testResult
          ? {
              ...testResult,
              TestType: toTestType(testResult.TestType),
              files: testResult.files.map((rf) => rf.file),
              ParentControlIds: [{ value: parentControlId }],
              Submitter: { value: testResult.Submitter, type: 'user' },
            }
          : undefined
      }
      defaultValues={{
        ...defaultValues,
        ParentControlIds: [{ value: parentControlId }],
        ...(inheritsWizardAssessment
          ? { AssessmentId: wizardAssessmentId }
          : {}),
        ...copiedDefaults,
      }}
      onSave={onSave}
      onDismiss={onDismiss}
      readOnly={!canModify}
      showAssessmentSelector={inheritsWizardAssessment && Id === undefined}
      disableControlSelect={Id !== undefined}
      renderTemplate={(renderProps) => (
        <ModalWrapper
          testId={'testResultModal'}
          {...renderProps}
          visible={true}
        />
      )}
      assessmentMode={assessmentMode}
    />
  );
};

const TestResultModal: FC<Props> = (props) => {
  if (props.assessmentMode === 'internal_audit_report') {
    return (
      <Modal
        data-testid={'testResultModal'}
        disableContentPaddings={true}
        visible={true}
        onDismiss={(event) => {
          if (event.detail.reason === 'overlay') {
            return;
          }
          props.onDismiss(false);
        }}
      >
        <ConnectedInternalAuditControlTestResultForm
          readonly={!!props.Id}
          isModalForm={true}
          navigateToResults={false}
          parentId={''}
          controlIds={[props.parentControlId]}
          id={props.Id}
          onDismiss={props.onDismiss}
        />
      </Modal>
    );
  }

  if (props.assessmentMode === 'oversight_assessment') {
    return (
      <Modal
        data-testid={'testResultModal'}
        disableContentPaddings={true}
        visible={true}
        onDismiss={(event) => {
          if (event.detail.reason === 'overlay') {
            return;
          }
          props.onDismiss(false);
        }}
      >
        <ConnectedControlTestOversightResultForm
          readonly={!!props.Id}
          isModalForm={true}
          navigateToResults={false}
          parentId={''}
          controlIds={[props.parentControlId]}
          id={props.Id}
          onDismiss={props.onDismiss}
          showAssessmentSelector={!props.Id}
        />
      </Modal>
    );
  }

  return <StandardTestResultModal {...props} />;
};

export default TestResultModal;
